import CryptoJS from 'crypto-js';
import { toast } from 'react-toastify';
import { all, call, put, takeLatest } from 'redux-saga/effects';
import env from '../../config/env';
import * as service from '../../services/auth.service';
import * as notificationService from '../../services/notification.service';
import { USER_ROLES } from '../../utils/constants';
// import { requestPermission } from '../../utils/notification';
import {
    getUserRequest,
    getUserSuccess,
    setGlobalHotelId,
    setHotelType,
    setNotificationData,
    setSettingsFormData,
    setVerifyUserName
} from '../slice';
import {
    FORGOT_PASSWORD_REQUEST,
    GET_NOTIFICATION_REQUEST,
    GET_USER_REQUEST,
    LOGIN_USER_REQUEST,
    LOGOUT_USER_REQUEST,
    REGISTER_USER_REQUEST,
    RESET_PASSWORD_REQUEST,
    // SUBSCRIBE_NOTIFICATION_REQUEST,
    UPDATE_NOTIFICATION_REQUEST,
    UPDATE_USER_REQUEST,
    VERIFY_USER_REQUEST
} from '../types';

function* loginUserRequestSaga(action) {
    try {
        const { data, navigate } = action.payload;
        const res = yield service.loginUser(data);

        localStorage.setItem('token', res.token);
        localStorage.setItem('data', res.data);
        if (res.trackerSecret) {
            localStorage.setItem('trackerSecret', res.trackerSecret);
        }

        toast.success('Login successfully');
        const viewData = JSON.parse(
            CryptoJS.AES.decrypt(res.data, env.cryptoSecret).toString(CryptoJS.enc.Utf8)
        );
        if ([USER_ROLES.foodCourtOwner, USER_ROLES.hotelOwner].includes(viewData.role)) {
            navigate('/hotels');
        } else {
            if (res.trackerSecret) {
                navigate('/gateway');
            } else {
                navigate('/dashboard');
            }
        }
    } catch (error) {
        toast.error(`Failed to login: ${error?.message}`);
    }
}

function* registerUserRequestSaga(action) {
    try {
        const { data, navigate } = action.payload;
        yield service.registerUser(data);
        toast.success('User registered successfully. Please verify your email');
        navigate('/login');
    } catch (error) {
        toast.error(`Failed to register user: ${error?.message}`);
    }
}

function* verifyUserRequestSaga(action) {
    const { data, navigate } = action.payload;
    try {
        const { name, ...rest } = data;
        const res = yield service.verifyUser(rest);

        localStorage.setItem('token', res.token);
        localStorage.setItem('data', res.data);
        if (res.trackerSecret) {
            localStorage.setItem('trackerSecret', res.trackerSecret);
        }

        toast.success('Verified successfully');
        yield put(setVerifyUserName(name));
        const viewData = JSON.parse(CryptoJS.AES.decrypt(res.data, env.cryptoSecret).toString(CryptoJS.enc.Utf8));
        if ([USER_ROLES.foodCourtOwner, USER_ROLES.hotelOwner].includes(viewData.role)) {
            navigate('/hotels');
        } else {
            if (res.trackerSecret) {
                navigate('/gateway');
            } else {
                navigate('/dashboard');
            }
        }

        yield put(getUserRequest({ navigate }));
    } catch (error) {
        toast.error(`Failed to verify email: ${error?.message}`);
        navigate('/login');
    }
}

// function* registerNotification(action) {
//     try {
//         const token = yield requestPermission();
//         console.log('Firebase token : ', token);
//         if (token) {
//             const payload = { token, platform: 'web' };
//             // if (action.payload?.customerId) {
//             payload.customerId = action.payload?.customerId;
//             // }
//             // if (action.payload?.userId) {
//             //     payload.userId = action.payload?.userId;
//             // }
//             yield call(notificationService.subscribe, payload);
//         }
//     } catch (error) {
//         console.error(`Failed to register notification : ${error?.message}`);
//     }
// }

function* forgotPasswordRequestSaga(action) {
    try {
        const { data, navigate } = action.payload;
        yield service.forgotPasswordUser(data);
        toast.success('Reset password email sent successfully');
        navigate('/login');
    } catch (error) {
        toast.error(`Failed to send: ${error?.message}`);
    }
}

function* resetPasswordRequestSaga(action) {
    try {
        const { data, navigate } = action.payload;
        yield service.resetPasswordUser(data);
        toast.success('Password reset successfully');
        navigate('/login');
    } catch (error) {
        toast.error(`Failed to reset password: ${error?.message}`);
    }
}

function* getUserRequestSaga(action) {
    try {
        const navigate = action.payload?.navigate;
        const res = yield service.getUser();
        yield put(getUserSuccess(res));

        if (navigate) {
            const viewData = {};
            if (
                [
                    USER_ROLES.hotelStaff,
                    USER_ROLES.foodCourtOwner,
                    USER_ROLES.foodCourtShopOwner,
                    USER_ROLES.foodCourtStaff
                ].includes(
                    res.role.toUpperCase()
                )
            ) {
                yield put(setGlobalHotelId(res.hotelId || viewData.hotelId));
                yield put(setHotelType(res.hotelType));
            }
        }
    } catch (error) {
        console.error(`Failed to get user: ${error?.message}`);
    }
}

function* updateUserRequestSaga(action) {
    try {
        const res = yield service.updateUser(action.payload);
        yield put(getUserSuccess(res));
        toast.success('User details updated successfully');
        yield put(setSettingsFormData(false));
    } catch (error) {
        console.error(`Failed to update user: ${error?.message}`);
        yield put(setSettingsFormData(false));
        toast.error('Failed to update user details');
    }
}

function* logoutUserRequestSaga() {
    try {
        if ('serviceWorker' in navigator) {
            const registrations = yield call([navigator.serviceWorker, 'getRegistrations']);
            for (const registration of registrations) {
                yield call([registration, 'unregister']);
            }
        }

        localStorage.clear();
        window.location.replace('/login');
    } catch (error) {
        console.error(`Failed to logout user: ${error?.message}`);
        toast.error('Failed to logout user');
    }
}

function* getNotificationRequestSaga() {
    try {
        const res = yield notificationService.fetch();
        yield put(setNotificationData({ data: res.rows, count: res.count }));
    } catch (error) {
        console.error(`Failed to fetch notification: ${error?.message}`);
        toast.error('Failed to fetch notification');
    }
}

function* updateNotificationRequestSaga() {
    try {
        yield notificationService.update();
        yield put(setNotificationData({ data: [], count: 0 }));
    } catch (error) {
        console.error(`Failed to update notification: ${error?.message}`);
        toast.error('Failed to set notifications as read');
    }
}

export default function* authSaga() {
    yield all([
        takeLatest(LOGIN_USER_REQUEST, loginUserRequestSaga),
        takeLatest(LOGOUT_USER_REQUEST, logoutUserRequestSaga),
        takeLatest(REGISTER_USER_REQUEST, registerUserRequestSaga),
        takeLatest(VERIFY_USER_REQUEST, verifyUserRequestSaga),
        takeLatest(FORGOT_PASSWORD_REQUEST, forgotPasswordRequestSaga),
        takeLatest(RESET_PASSWORD_REQUEST, resetPasswordRequestSaga),
        takeLatest(GET_USER_REQUEST, getUserRequestSaga),
        takeLatest(UPDATE_USER_REQUEST, updateUserRequestSaga),
        takeLatest(GET_NOTIFICATION_REQUEST, getNotificationRequestSaga),
        takeLatest(UPDATE_NOTIFICATION_REQUEST, updateNotificationRequestSaga)
        // takeLatest(SUBSCRIBE_NOTIFICATION_REQUEST, registerNotification)
    ]);
}
